import React, { useState } from 'react'
import type { TeamRollupOut, TeamSeasonStatsOut, HeadToHeadOut, TimeSplitScope } from '../types'
import { HeadToHeadCard } from './HeadToHeadCard'
import { TimeSplitStatsCard } from './TimeSplitStatsCard'
import { ShotStatsCard } from './ShotStatsCard'
import { FormAnalysisCard } from './FormAnalysisCard'
import { useFixtureTimeSplits } from '../swr-config'

interface TeamsTabProps {
  teams: TeamRollupOut[] | null
  seasonStats: TeamSeasonStatsOut[] | null
  headToHead: HeadToHeadOut | null
  fixtureId?: number
}

type WindowKey = 'season' | 'last10' | 'last5' | 'last3'
type ViewMode = 'per90' | 'season' | 'time-splits' | 'shots'

// Stats that come from the main window (overall stats)
const OVERALL_STATS = [
  { key: 'avg_goals_for', label: 'Goals' },
  { key: 'avg_goals_against', label: 'Goals Conceded' },
  { key: 'avg_xg', label: 'xG' },
  { key: 'avg_xg_against', label: 'xG Against' },
  { key: 'avg_corners', label: 'Corners' },
  { key: 'avg_corners_against', label: 'Corners Against' },
  { key: 'avg_shots', label: 'Shots' },
  { key: 'avg_shots_on_target', label: 'SOT' },
  { key: 'avg_saves', label: 'Saves' },
  { key: 'avg_tackles', label: 'Tackles' },
  { key: 'total_cards', label: 'Cards', perMatch: true, computed: true },
  { key: 'offsides', label: 'Offsides', perMatch: true },
  { key: 'avg_fouls', label: 'Fouls' },
  { key: 'avg_fouls_against', label: 'Fouls Against' },
  { key: 'avg_possession', label: 'Possession %' },
  { key: 'pass_accuracy_pct', label: 'Pass Acc %', format: 'pct_raw' },
  { key: 'btts_rate', label: 'BTTS %', format: 'pct' },
  { key: 'clean_sheet_rate', label: 'Clean Sheet %', computed: true, format: 'pct_raw' },
] as const

// Venue-specific stats - we show home team's home stats vs away team's away stats
const VENUE_STATS = [
  { key: 'avg_goals_for', homeKey: 'avg_goals_for_home', awayKey: 'avg_goals_for_away', label: 'Goals (H/A)' },
  { key: 'avg_goals_against', homeKey: 'avg_goals_against_home', awayKey: 'avg_goals_against_away', label: 'Conceded (H/A)' },
] as const


function TrendArrow({ current, season }: { current: number | null; season: number | null }) {
  if (current === null || season === null) return null

  const diff = current - season
  const threshold = 0.01 // Small threshold to account for floating point

  if (Math.abs(diff) < threshold) {
    return <span style={{ color: 'var(--muted)', marginLeft: 4 }}>—</span>
  } else if (diff > 0) {
    return <span style={{ color: 'var(--green)', marginLeft: 4 }}>▲</span>
  } else {
    return <span style={{ color: 'var(--red)', marginLeft: 4 }}>▼</span>
  }
}

function FormDisplay({ form }: { form: string | null | undefined }) {
  if (!form) return <span className="small">—</span>
  const recent = form.slice(-10)
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {recent.split('').map((char, i) => (
        <span
          key={i}
          style={{
            width: 18,
            height: 18,
            borderRadius: 4,
            display: 'grid',
            placeItems: 'center',
            fontSize: 10,
            fontWeight: 700,
            background:
              char === 'W'
                ? 'rgba(74,222,128,.25)'
                : char === 'D'
                  ? 'rgba(251,191,36,.25)'
                  : 'rgba(251,113,133,.25)',
            color:
              char === 'W'
                ? 'var(--green)'
                : char === 'D'
                  ? 'var(--yellow)'
                  : 'var(--red)',
          }}
        >
          {char}
        </span>
      ))}
    </div>
  )
}

export function TeamsTab({ teams, seasonStats, headToHead, fixtureId }: TeamsTabProps) {
  const [window, setWindow] = useState<WindowKey>('season')
  const [viewMode, setViewMode] = useState<ViewMode>('per90')
  const [timeSplitScope, setTimeSplitScope] = useState<TimeSplitScope>('last5')

  // Fetch time-split data with the current scope
  const { data: timeSplitContext } = useFixtureTimeSplits(fixtureId ?? null, timeSplitScope)

  if (!teams || teams.length === 0) {
    return (
      <div className="small" style={{ marginTop: 10 }}>
        No rollups. Ingest finished fixtures and run rollups.
      </div>
    )
  }

  const windowLabel = { season: 'Season', last10: 'L10', last5: 'L5', last3: 'L3' }

  return (
    <div className="teams-tab">
      {/* Head-to-head card */}
      {headToHead && <HeadToHeadCard h2h={headToHead} />}

      {/* View mode toggle */}
      <div className="tab-filter-bar">
        <button
          className={`chip ${viewMode === 'per90' ? 'chip-active' : ''}`}
          onClick={() => setViewMode('per90')}
        >
          Per 90 Stats
        </button>
        <button
          className={`chip ${viewMode === 'season' ? 'chip-active' : ''}`}
          onClick={() => setViewMode('season')}
        >
          Season Overview
        </button>
        <button
          className={`chip ${viewMode === 'time-splits' ? 'chip-active' : ''}`}
          onClick={() => setViewMode('time-splits')}
        >
          Time Splits
        </button>
        <button
          className={`chip ${viewMode === 'shots' ? 'chip-active' : ''}`}
          onClick={() => setViewMode('shots')}
        >
          Shot Analysis
        </button>
      </div>

      {viewMode === 'per90' && teams.length >= 2 && (() => {
        const homeTeam = teams[0]
        const awayTeam = teams[1]
        const homeData = homeTeam.windows[window] || {}
        const awayData = awayTeam.windows[window] || {}
        const homeSeasonData = homeTeam.windows.season || {}
        const awaySeasonData = awayTeam.windows.season || {}
        const homeMatches = homeData.matches || 0
        const awayMatches = awayData.matches || 0
        const homeSeasonMatches = homeSeasonData.matches || 0
        const awaySeasonMatches = awaySeasonData.matches || 0
        const showTrend = window !== 'season'


        const getStatValue = (
          sourceData: Record<string, any>,
          key: string,
          matchCount: number,
          perMatch?: boolean
        ) => {
          if (key === 'total_cards') {
            const yellow = (sourceData.cards_y as number) || 0
            const red = (sourceData.cards_r as number) || 0
            const total = yellow + red
            if (perMatch && matchCount > 0) {
              return total / matchCount
            }
            return total
          }
          if (key === 'clean_sheet_rate') {
            const cleanSheets = (sourceData.clean_sheets as number) || 0
            if (matchCount > 0) {
              return (cleanSheets / matchCount) * 100
            }
            return null
          }
          const val = sourceData[key]
          if (perMatch && matchCount > 0 && typeof val === 'number') {
            return val / matchCount
          }
          return typeof val === 'number' ? val : null
        }

        const formatVal = (val: number | null | undefined, format?: string) => {
          if (val === null || val === undefined) return '—'
          if (format === 'pct') return `${(val * 100).toFixed(0)}%`
          if (format === 'pct_raw') return `${val.toFixed(0)}%`
          return val.toFixed(1)
        }

        return (
          <>
            {/* Window toggle for per90 */}
            <div className="row" style={{ gap: 8, marginBottom: 10 }}>
              {(['season', 'last10', 'last5', 'last3'] as WindowKey[]).map((w) => (
                <button
                  key={w}
                  className={`chip ${window === w ? 'chip-active' : ''}`}
                  onClick={() => setWindow(w)}
                >
                  {windowLabel[w]}
                </button>
              ))}
            </div>

            {/* Team header row */}
            <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
              <div className="card-inner">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <span className="badge">
                      {homeTeam.team.crest_url ? <img src={homeTeam.team.crest_url} alt="" /> : 'H'}
                    </span>
                    <div>
                      <strong>{homeTeam.team.name}</strong>
                      <div className="small">{homeMatches} matches</div>
                    </div>
                  </div>
                  <div style={{ color: 'var(--muted)', fontWeight: 600 }}>vs</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
                    <div style={{ textAlign: 'right' }}>
                      <strong>{awayTeam.team.name}</strong>
                      <div className="small">{awayMatches} matches</div>
                    </div>
                    <span className="badge">
                      {awayTeam.team.crest_url ? <img src={awayTeam.team.crest_url} alt="" /> : 'A'}
                    </span>
                  </div>
                </div>

                {/* Stats comparison rows */}
                <div className="live-stats">
                  {OVERALL_STATS.map((row) => {
                    const homeVal = getStatValue(homeData, row.key, homeMatches, 'perMatch' in row ? row.perMatch : undefined)
                    const awayVal = getStatValue(awayData, row.key, awayMatches, 'perMatch' in row ? row.perMatch : undefined)
                    const homeSeasonVal = getStatValue(homeSeasonData, row.key, homeSeasonMatches, 'perMatch' in row ? row.perMatch : undefined)
                    const awaySeasonVal = getStatValue(awaySeasonData, row.key, awaySeasonMatches, 'perMatch' in row ? row.perMatch : undefined)
                    const format = 'format' in row ? row.format : undefined

                    return (
                      <div key={row.key} className="stat-row">
                        <span className="stat-value" style={{ textAlign: 'left' }}>
                          {formatVal(homeVal, format)}
                          {showTrend && <TrendArrow current={homeVal} season={homeSeasonVal} />}
                        </span>
                        <span className="stat-label">{row.label}</span>
                        <span className="stat-value" style={{ textAlign: 'right' }}>
                          {showTrend && <TrendArrow current={awayVal} season={awaySeasonVal} />}
                          {formatVal(awayVal, format)}
                        </span>
                      </div>
                    )
                  })}

                  {/* Venue-specific stats - home team's home stats vs away team's away stats */}
                  {VENUE_STATS.map((row) => {
                    // Use the home/away specific keys from the main window data
                    const homeVal = homeData[row.homeKey] as number | null
                    const awayVal = awayData[row.awayKey] as number | null

                    return (
                      <div key={row.key} className="stat-row">
                        <span className="stat-value">{formatVal(homeVal)}</span>
                        <span className="stat-label">{row.label}</span>
                        <span className="stat-value">{formatVal(awayVal)}</span>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </>
        )
      })()}

      {viewMode === 'season' && seasonStats && seasonStats.length >= 2 && (() => {
        const home = seasonStats[0]
        const away = seasonStats[1]

        const getTeamStats = (s: typeof home) => {
          const totalPlayed = s.played_home + s.played_away
          const totalWins = s.wins_home + s.wins_away
          const totalDraws = s.draws_home + s.draws_away
          const totalLosses = s.losses_home + s.losses_away
          const totalGoalsFor = s.goals_for_home + s.goals_for_away
          const totalGoalsAgainst = s.goals_against_home + s.goals_against_away
          const totalCleanSheets = s.clean_sheets_home + s.clean_sheets_away
          const totalFailedToScore = s.failed_to_score_home + s.failed_to_score_away
          const preferredFormation = s.lineups?.[0]?.formation || '—'
          const raw = s.raw_json || {}
          const goalsData = raw.goals || {}
          const avgGoalsFor = goalsData.for?.average?.total || '—'
          const avgGoalsAgainst = goalsData.against?.average?.total || '—'
          const biggest = s.biggest || {}
          const streakWins = biggest.streak?.wins || 0
          const streakDraws = biggest.streak?.draws || 0
          const streakLosses = biggest.streak?.loses || 0

          return {
            totalPlayed, totalWins, totalDraws, totalLosses,
            totalGoalsFor, totalGoalsAgainst, totalCleanSheets, totalFailedToScore,
            preferredFormation, avgGoalsFor, avgGoalsAgainst,
            streakWins, streakDraws, streakLosses,
            penaltiesScored: s.penalties_scored,
            penaltiesMissed: s.penalties_missed,
          }
        }

        const homeStats = getTeamStats(home)
        const awayStats = getTeamStats(away)

        return (
          <div className="card" style={{ marginTop: 10, background: 'rgba(255,255,255,.03)' }}>
            <div className="card-inner">
              {/* Team header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                  <span className="badge">
                    {home.team.crest_url ? <img src={home.team.crest_url} alt="" /> : 'H'}
                  </span>
                  <div>
                    <strong>{home.team.name}</strong>
                    <div className="small">{homeStats.preferredFormation}</div>
                  </div>
                </div>
                <div style={{ color: 'var(--muted)', fontWeight: 600 }}>vs</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
                  <div style={{ textAlign: 'right' }}>
                    <strong>{away.team.name}</strong>
                    <div className="small">{awayStats.preferredFormation}</div>
                  </div>
                  <span className="badge">
                    {away.team.crest_url ? <img src={away.team.crest_url} alt="" /> : 'A'}
                  </span>
                </div>
              </div>

              {/* Form comparison */}
              <div style={{ marginBottom: 12 }}>
                <div className="small" style={{ marginBottom: 6, textAlign: 'center' }}>Form (last 10)</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormDisplay form={home.form} />
                  <FormDisplay form={away.form} />
                </div>
              </div>

              {/* Stats comparison rows */}
              <div className="live-stats">
                <div className="stat-row">
                  <span className="stat-value">{homeStats.totalPlayed}</span>
                  <span className="stat-label">Played</span>
                  <span className="stat-value">{awayStats.totalPlayed}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.totalWins}W-{homeStats.totalDraws}D-{homeStats.totalLosses}L</span>
                  <span className="stat-label">Record</span>
                  <span className="stat-value">{awayStats.totalWins}W-{awayStats.totalDraws}D-{awayStats.totalLosses}L</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.totalGoalsFor}</span>
                  <span className="stat-label">Goals For</span>
                  <span className="stat-value">{awayStats.totalGoalsFor}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.totalGoalsAgainst}</span>
                  <span className="stat-label">Goals Against</span>
                  <span className="stat-value">{awayStats.totalGoalsAgainst}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.avgGoalsFor}</span>
                  <span className="stat-label">Avg Goals/Game</span>
                  <span className="stat-value">{awayStats.avgGoalsFor}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.avgGoalsAgainst}</span>
                  <span className="stat-label">Avg Conceded/Game</span>
                  <span className="stat-value">{awayStats.avgGoalsAgainst}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.totalCleanSheets}</span>
                  <span className="stat-label">Clean Sheets</span>
                  <span className="stat-value">{awayStats.totalCleanSheets}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.totalFailedToScore}</span>
                  <span className="stat-label">Failed to Score</span>
                  <span className="stat-value">{awayStats.totalFailedToScore}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.penaltiesScored}/{homeStats.penaltiesScored + homeStats.penaltiesMissed}</span>
                  <span className="stat-label">Penalties</span>
                  <span className="stat-value">{awayStats.penaltiesScored}/{awayStats.penaltiesScored + awayStats.penaltiesMissed}</span>
                </div>
                <div className="stat-row">
                  <span className="stat-value">{homeStats.streakWins}W·{homeStats.streakDraws}D·{homeStats.streakLosses}L</span>
                  <span className="stat-label">Best Streak</span>
                  <span className="stat-value">{awayStats.streakWins}W·{awayStats.streakDraws}D·{awayStats.streakLosses}L</span>
                </div>
              </div>

              {/* Over/Under comparison */}
              {(home.under_over_for || away.under_over_for) && (
                <div style={{ marginTop: 12 }}>
                  <div className="small" style={{ marginBottom: 6, textAlign: 'center' }}>Goals Scored - Over/Under</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1 }}>
                      {home.under_over_for && Object.entries(home.under_over_for).map(([line, data]) => (
                        <div
                          key={line}
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: 'rgba(255,255,255,.05)',
                            fontSize: 10,
                          }}
                        >
                          <span style={{ color: 'var(--muted)' }}>O{line}</span>{' '}
                          <span style={{ color: 'var(--green)' }}>{data.over}</span>
                          <span style={{ color: 'var(--muted)' }}>/</span>
                          <span style={{ color: 'var(--red)' }}>{data.under}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
                      {away.under_over_for && Object.entries(away.under_over_for).map(([line, data]) => (
                        <div
                          key={line}
                          style={{
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: 'rgba(255,255,255,.05)',
                            fontSize: 10,
                          }}
                        >
                          <span style={{ color: 'var(--muted)' }}>O{line}</span>{' '}
                          <span style={{ color: 'var(--green)' }}>{data.over}</span>
                          <span style={{ color: 'var(--muted)' }}>/</span>
                          <span style={{ color: 'var(--red)' }}>{data.under}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      })()}

      {viewMode === 'season' && !seasonStats && (
        <div className="small" style={{ marginTop: 10 }}>
          Loading season stats...
        </div>
      )}

      {/* Form Analysis Card - show in season view when we have team data */}
      {viewMode === 'season' && fixtureId && teams && teams.length >= 2 && (
        <FormAnalysisCard
          fixtureId={fixtureId}
          homeTeamName={teams[0].team.name}
          awayTeamName={teams[1].team.name}
          homeCrest={teams[0].team.crest_url}
          awayCrest={teams[1].team.crest_url}
        />
      )}

      {viewMode === 'time-splits' && teams.length >= 2 && (
        timeSplitContext ? (
          <TimeSplitStatsCard
            context={timeSplitContext}
            homeTeamName={teams[0].team.name}
            awayTeamName={teams[1].team.name}
            scope={timeSplitScope}
            onScopeChange={setTimeSplitScope}
          />
        ) : (
          <div className="small" style={{ marginTop: 10 }}>
            Loading time-split data...
          </div>
        )
      )}

      {viewMode === 'shots' && teams.length >= 2 && (() => {
        const homeTeam = teams[0]
        const awayTeam = teams[1]
        const homeData = homeTeam.windows[window] || {}
        const awayData = awayTeam.windows[window] || {}

        return (
          <>
            {/* Window toggle for shots view */}
            <div className="row" style={{ gap: 8, marginBottom: 10 }}>
              {(['season', 'last10', 'last5', 'last3'] as WindowKey[]).map((w) => (
                <button
                  key={w}
                  className={`chip ${window === w ? 'chip-active' : ''}`}
                  onClick={() => setWindow(w)}
                >
                  {windowLabel[w]}
                </button>
              ))}
            </div>

            <ShotStatsCard
              homeTeam={homeTeam.team.name}
              awayTeam={awayTeam.team.name}
              homeStats={{
                avg_shots: homeData.avg_shots ?? null,
                avg_shots_on_target: homeData.avg_shots_on_target ?? null,
                avg_shots_against: homeData.avg_shots_against ?? null,
                avg_xg: homeData.avg_xg ?? null,
                avg_xg_against: homeData.avg_xg_against ?? null,
                avg_shots_home: homeData.avg_shots_home ?? null,
                avg_shots_away: homeData.avg_shots_away ?? null,
                avg_shots_against_home: homeData.avg_shots_against_home ?? null,
                avg_shots_against_away: homeData.avg_shots_against_away ?? null,
              }}
              awayStats={{
                avg_shots: awayData.avg_shots ?? null,
                avg_shots_on_target: awayData.avg_shots_on_target ?? null,
                avg_shots_against: awayData.avg_shots_against ?? null,
                avg_xg: awayData.avg_xg ?? null,
                avg_xg_against: awayData.avg_xg_against ?? null,
                avg_shots_home: awayData.avg_shots_home ?? null,
                avg_shots_away: awayData.avg_shots_away ?? null,
                avg_shots_against_home: awayData.avg_shots_against_home ?? null,
                avg_shots_against_away: awayData.avg_shots_against_away ?? null,
              }}
              homeCrest={homeTeam.team.crest_url}
              awayCrest={awayTeam.team.crest_url}
              window={window}
            />
          </>
        )
      })()}
    </div>
  )
}
